export const GUIDE_CATEGORIES = [
  { key: 'setup', label: 'Install & Activation', blurb: 'Getting the program on your computer and unlocked.' },
  { key: 'sync', label: 'Sync & Cloud', blurb: 'FamilySync, Ancestry sign-in, and TreeVault.' },
  { key: 'media', label: 'Photos, Charts & Books', blurb: 'Media links, printing, and publishing.' },
  { key: 'care', label: 'Backups & Tree Health', blurb: 'Keeping years of research safe.' },
];

export const GUIDES = [
  {
    slug: 'install-family-tree-maker-on-windows',
    category: 'setup',
    title: 'Installing Family Tree Maker on Windows 10 and 11',
    summary:
      'A calm, step-by-step walk through the Windows installer, from the download file to your first open tree.',
    readTime: '6 min read',
    sections: [
      {
        heading: 'Before you start',
        body: [
          'Make sure you are signed in to Windows with an account that can install software. On a home computer this is usually the first account that was created. Close other programs, and if you are replacing an older edition, make a backup of every tree first.',
          'Have your serial number ready. It is in the purchase confirmation email, or printed on the card inside a boxed copy.',
        ],
      },
      {
        heading: 'Run the installer',
        steps: [
          'Open your Downloads folder and double-click the Family Tree Maker setup file.',
          'If Windows asks whether to allow the app to make changes, choose Yes.',
          'Accept the licence agreement and keep the suggested install location unless you have a reason to change it.',
          'Wait for the progress bar to finish. On older computers this can take several minutes.',
          'Leave the box ticked to launch Family Tree Maker, then click Finish.',
        ],
      },
      {
        heading: 'If the installer stops part-way',
        body: [
          'A stalled install is almost always caused by security software scanning the files as they are written. Temporarily pause real-time protection, restart the computer, and run the installer again with a right-click and Run as administrator.',
          'If you see a message about a missing .NET component, run Windows Update until no further updates are offered, restart, and try again.',
        ],
      },
      {
        heading: 'Opening your first tree',
        body: [
          'On first launch you can start a new tree, import a GEDCOM file from another program, restore a Family Tree Maker backup, or download a tree from Ancestry. If you are moving from an older edition, restoring the backup you just made is the safest route.',
        ],
      },
    ],
  },
  {
    slug: 'install-family-tree-maker-on-mac',
    category: 'setup',
    title: 'Installing Family Tree Maker on a Mac',
    summary:
      'Drag-and-drop installation, macOS security prompts, and what to do on Macs with Apple silicon.',
    readTime: '5 min read',
    sections: [
      {
        heading: 'Download the Mac edition',
        body: [
          'Family Tree Maker has separate installers for Windows and macOS. Check that the file you downloaded ends in .dmg. If it ends in .exe, you have the Windows version and need to download again from your account.',
        ],
      },
      {
        heading: 'Install the app',
        steps: [
          'Double-click the .dmg file to open it.',
          'Drag the Family Tree Maker icon onto the Applications folder shortcut.',
          'Eject the installer from the Finder sidebar once copying finishes.',
          'Open Applications and double-click Family Tree Maker.',
        ],
      },
      {
        heading: 'Getting past the security warning',
        body: [
          'macOS sometimes warns that an app was downloaded from the internet. Choose Open if offered. If the button is missing, go to System Settings › Privacy & Security, scroll down, and click Open Anyway next to the Family Tree Maker message.',
          'When the program asks for access to your Documents or Photos folders, allow it. Without that permission, media files may not attach correctly.',
        ],
      },
      {
        heading: 'Apple silicon Macs',
        body: [
          'Current editions run natively on M-series Macs. Older editions may ask to install Rosetta the first time they open; accept the prompt and the program will start normally afterwards.',
        ],
      },
    ],
  },
  {
    slug: 'activation-and-serial-number-problems',
    category: 'setup',
    title: 'Fixing Activation and Serial Number Problems',
    summary:
      'What to check when your serial number is rejected or the program says the activation limit has been reached.',
    readTime: '5 min read',
    sections: [
      {
        heading: 'Type it carefully',
        body: [
          'Serial numbers are usually grouped in blocks separated by dashes. The letter O and the number 0, and the letter I and the number 1, are easy to mix up. Copy and paste from your purchase email rather than typing it where possible, and make sure no space has slipped in at the start or end.',
        ],
      },
      {
        heading: 'Check that the edition matches',
        body: [
          'A serial number for one edition will not unlock another. An upgrade key for 2024 needs an older edition to have been installed and registered first, and a Windows key is not always valid for the Mac version. Compare the edition named in your receipt with the one you installed.',
        ],
      },
      {
        heading: '“Activation limit reached”',
        body: [
          'Licences allow a set number of computers. If you have replaced a computer or reinstalled Windows several times, the old installations may still be counted. The publisher can reset the count for you; have your serial number and purchase details ready when you ask.',
        ],
      },
      {
        heading: 'Connection-related failures',
        steps: [
          'Confirm the computer can open ordinary websites.',
          'Temporarily pause any VPN, proxy, or strict firewall.',
          'Check the computer clock is set to the correct date and time zone.',
          'Restart Family Tree Maker and try activation again.',
        ],
      },
    ],
  },
  {
    slug: 'fix-familysync-errors',
    category: 'sync',
    title: 'How to Fix FamilySync Errors and Stuck Syncs',
    summary:
      'A practical checklist for syncs that hang, fail, or report conflicts between your desktop tree and Ancestry.',
    readTime: '7 min read',
    sections: [
      {
        heading: 'Start with the basics',
        steps: [
          'Install the latest Family Tree Maker update from the Help menu.',
          'Restart the computer to clear any half-finished sync processes.',
          'Check that you can sign in to Ancestry in a web browser.',
          'Make a backup of the tree before trying anything else.',
        ],
      },
      {
        heading: 'Why syncs get stuck',
        body: [
          'FamilySync compares two copies of the same tree and sends only the differences. Large batches of changes, such as merging hundreds of people or attaching a big folder of photos, take much longer than usual and can time out on slower connections.',
          'Editing the same person on Ancestry and in Family Tree Maker between syncs creates a conflict that the program has to stop and ask about. Try to do your edits in one place at a time.',
        ],
      },
      {
        heading: 'When a sync keeps failing',
        body: [
          'Check the sync log, available from the sync status window, for the person or media file the sync stopped on. Very large or unusual image files are frequent culprits. Detach the file, sync again, and reattach it afterwards.',
          'If nothing works, the cleanest fix is often to unlink the tree, make a fresh download from Ancestry, and compare it with your backup. Do not delete either copy until you have checked that the new one is complete.',
        ],
      },
    ],
  },
  {
    slug: 'ancestry-sign-in-problems',
    category: 'sync',
    title: 'Ancestry Sign-In Problems Inside Family Tree Maker',
    summary:
      'Why the Ancestry login window keeps reappearing, and how to get web search and hints working again.',
    readTime: '4 min read',
    sections: [
      {
        heading: 'Confirm the account itself',
        body: [
          'Sign in to Ancestry in a normal web browser with the same email and password. If that fails, reset the password on the Ancestry website first; Family Tree Maker cannot sign in with details the website does not accept.',
        ],
      },
      {
        heading: 'Clear the saved login',
        steps: [
          'In Family Tree Maker, open the Help menu and sign out of Ancestry.',
          'Close the program completely and reopen it.',
          'Sign in again when prompted, typing the password rather than pasting it.',
        ],
      },
      {
        heading: 'Still looping?',
        body: [
          'Security software that inspects web traffic can block the embedded login window. Add Family Tree Maker as a trusted program, or pause web protection briefly to test. Also check that Windows or macOS is fully up to date, as the login window depends on system web components.',
        ],
      },
    ],
  },
  {
    slug: 'treevault-cloud-backup-guide',
    category: 'sync',
    title: 'A Beginner’s Guide to TreeVault Cloud Storage',
    summary:
      'What TreeVault is, how it differs from FamilySync, and how to use it to protect your tree.',
    readTime: '5 min read',
    sections: [
      {
        heading: 'What TreeVault does',
        body: [
          'TreeVault is the publisher’s optional cloud service for keeping copies of your Family Tree Maker files online. Unlike FamilySync, it does not create an Ancestry tree; it simply stores your tree so you can restore it on another computer or after a hard drive failure.',
        ],
      },
      {
        heading: 'Uploading a tree',
        steps: [
          'Open the tree you want to protect.',
          'Choose the TreeVault option from the File menu or the Plan workspace.',
          'Sign in with your TreeVault account.',
          'Follow the prompts to upload, and leave the program open until it confirms completion.',
        ],
      },
      {
        heading: 'Good habits',
        body: [
          'A cloud copy is only as fresh as your last upload. Make it part of the end of each research session, and keep at least one local backup on an external drive as well. Check the publisher’s current terms for storage limits and pricing.',
        ],
      },
    ],
  },
  {
    slug: 'relink-missing-media',
    category: 'media',
    title: 'Relinking Missing Photos and Media Files',
    summary:
      'Bring back pictures that now show a broken-link icon, even when hundreds of files are affected.',
    readTime: '6 min read',
    sections: [
      {
        heading: 'Why links break',
        body: [
          'Family Tree Maker does not copy photos into the tree file by default; it remembers where each one lives. Moving the folder, renaming a drive, restoring onto a new computer, or switching cloud-storage apps all change that location and break the link.',
        ],
      },
      {
        heading: 'Use Find Missing Media',
        steps: [
          'Go to the Media workspace.',
          'Open the Media menu and choose Find Missing Media.',
          'Click Search and point the program at the folder where your photos now live.',
          'Review the matches and click Relink.',
        ],
      },
      {
        heading: 'Prevent it next time',
        body: [
          'Keep all family photos under one top-level folder on the computer’s internal drive, and avoid renaming files after they are attached. When you move to a new computer, use a backup that includes media files so everything travels together.',
        ],
      },
    ],
  },
  {
    slug: 'printing-charts-and-family-books',
    category: 'media',
    title: 'Printing Wall Charts and Family History Books',
    summary:
      'Page setup, poster printing, and PDF export tips for charts and books that look good on paper.',
    readTime: '6 min read',
    sections: [
      {
        heading: 'Choose the right chart',
        body: [
          'Pedigree charts show direct ancestors; descendant charts show everyone who came after a couple; fan charts squeeze many generations into a compact semicircle. For a reunion wall chart, a descendant chart from the oldest common couple is usually the crowd-pleaser.',
        ],
      },
      {
        heading: 'Save as PDF first',
        body: [
          'Rather than sending a large chart straight to the printer, use Share › Export to PDF. You can check every page on screen, email the file to relatives, or take it to a print shop for poster-size output.',
        ],
      },
      {
        heading: 'Books that come out right',
        steps: [
          'Start with a short book of a single family line to learn the layout tools.',
          'Add a cover image and a table of contents before generating the index.',
          'Preview every chapter, checking photo placement and page breaks.',
          'Export to PDF and proofread before ordering printed copies.',
        ],
      },
    ],
  },
  {
    slug: 'backup-and-restore-your-tree',
    category: 'care',
    title: 'Backing Up and Restoring Your Family Tree',
    summary:
      'The one habit that protects years of research — and how to restore a backup when you need it.',
    readTime: '5 min read',
    sections: [
      {
        heading: 'Make a complete backup',
        steps: [
          'Open the tree and choose File › Backup.',
          'Tick the option to include media files.',
          'Choose a location on an external drive or USB stick.',
          'Give the file a name that includes today’s date.',
        ],
      },
      {
        heading: 'The three-copy rule',
        body: [
          'Keep the working tree on your computer, a backup on an external drive, and a third copy somewhere else — a cloud service, or a drive kept at a relative’s house. A fire, theft, or failed drive then never costs you everything.',
        ],
      },
      {
        heading: 'Restoring a backup',
        body: [
          'Choose File › Restore, select the backup file, and give the restored tree a new name so it does not overwrite anything. Open it, check a few people and photos, and only then retire the older copy.',
        ],
      },
    ],
  },
  {
    slug: 'move-family-tree-maker-to-new-computer',
    category: 'care',
    title: 'Moving Family Tree Maker to a New Computer',
    summary:
      'Transfer trees, photos, and your licence to a replacement computer without losing anything.',
    readTime: '6 min read',
    sections: [
      {
        heading: 'On the old computer',
        steps: [
          'Update Family Tree Maker and finish any pending sync.',
          'Create a backup of each tree with media included.',
          'Copy the backups and your photo folders to an external drive.',
          'Write down your serial number.',
        ],
      },
      {
        heading: 'On the new computer',
        steps: [
          'Install and activate Family Tree Maker.',
          'Copy your photo folders into the same relative location as before.',
          'Restore each backup from the external drive.',
          'Run Find Missing Media if any pictures show a broken link.',
        ],
      },
      {
        heading: 'Synced trees',
        body: [
          'If your tree is linked to Ancestry, restore the backup rather than downloading a fresh copy, so the existing sync link is preserved. Run one sync on the new computer and confirm it completes before using the old machine again.',
        ],
      },
    ],
  },
  {
    slug: 'keep-your-tree-file-healthy',
    category: 'care',
    title: 'Keeping Your Tree File Fast and Healthy',
    summary:
      'Compacting, repairing, and the everyday habits that prevent slowdowns and file damage.',
    readTime: '4 min read',
    sections: [
      {
        heading: 'Signs of trouble',
        body: [
          'A tree that opens slowly, freezes when you switch workspaces, or shows people with blank names is telling you something. Act early: small problems are far easier to repair than a badly damaged file.',
        ],
      },
      {
        heading: 'Compact and repair',
        steps: [
          'Make a backup first.',
          'Choose File › Compact File and let it finish.',
          'If problems remain, use the repair option offered in the same area.',
          'Reopen the tree and check that recent edits are still present.',
        ],
      },
      {
        heading: 'Everyday habits',
        body: [
          'Keep tree files on the internal drive rather than inside a syncing cloud folder, always close the program normally, and never interrupt a sync or backup. Those three habits prevent most of the damage we see.',
        ],
      },
    ],
  },
];

export const guidePath = (slug) => `/guides/${slug}`;
export const findGuide = (slug) => GUIDES.find((g) => g.slug === slug);
export const guidesIn = (key) => GUIDES.filter((g) => g.category === key);
export const categoryLabel = (key) => GUIDE_CATEGORIES.find((c) => c.key === key)?.label || '';
